import React, { useState, useEffect } from 'react';
import { Formik, Form, useField } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Flex, Text } from '@radix-ui/themes';
import { FaInfoCircle, FaCheckCircle, FaEye, FaEyeSlash } from 'react-icons/fa';
import axiosInstance from '../../config/axiosConfig';
import loginLogo from '../../assets/Luka-Healthcare.png';
import signImage from '../../assets/sign-image.svg';
import logoIMCAS from '../../assets/imacs_logo.svg';

const resetPasswordSchema = Yup.object({
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/\d/, 'Password must contain at least one number')
    .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character')
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
    .required('Please confirm your password'),
});

const ResetPassword = () => {
  const [status, setStatus] = useState('form'); // 'form', 'loading', 'success', 'error'
  const [message, setMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token');
  const identifier = searchParams.get('identifier');

  useEffect(() => {
    if (!token || !identifier) {
      setStatus('error');
      setMessage('Invalid reset link. Please request a new password reset.');
    }
  }, [token, identifier]);

  const handleSubmit = async (values, { setSubmitting }) => {
    setStatus('loading');
    
    try {
      const response = await axiosInstance.post('/Account/change-password', {
        password: values.password,
        confirmPassword: values.confirmPassword,
        identifier: identifier,
        token: token,
      });

      if (response.data.result) {
        setStatus('success');
        setMessage('Your password has been reset successfully!');
        
        // Redirect to login page after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setStatus('error');
        setMessage(response.data.errorResponse?.message || 'Failed to reset password. Please try again or request a new reset link.');
      }
    } catch (error) {
      setStatus('error');
      setMessage(error?.response?.data?.errorResponse?.message || 'Something went wrong. Please try again or request a new reset link.');
    }
    
    setSubmitting(false);
  };

  const PasswordField = ({ label, showPassword, togglePassword, ...props }) => {
    const [field, meta] = useField(props);
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        <div className="relative">
          <input
            {...field}
            {...props}
            type={showPassword ? 'text' : 'password'}
            className={`mt-1 block w-full px-4 py-3 pr-12 border rounded-md shadow-sm ${
              meta.touched && meta.error ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-blue-500"
            }`}
          />
          <button
            type="button"
            onClick={togglePassword}
            className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-600 hover:text-gray-800"
          >
            {showPassword ? <FaEyeSlash /> : <FaEye />}
          </button>
        </div>
        {meta.touched && meta.error && (
          <div className="mt-2 text-sm text-red-500">{meta.error}</div>
        )}
      </div>
    );
  };

  if (status === 'success') {
    return (
      <Flex direction={'row'} className='min-h-screen w-screen'>
        {/* Left decorative panel — hidden on mobile, visible on lg+ */}
        <Box className='hidden lg:flex h-screen bg-[#ecf2f1] w-[41.5%] sticky top-0'>
          <Flex gap={'6'} direction={'column'} align={'center'} className='h-full w-full'>
            <img className='w-[70%] my-24' src={loginLogo} alt="" />
            <img className='w-[88%] mt-0' src={signImage} alt="" />
          </Flex>
        </Box>
        {/* Right panel — full width on mobile */}
        <Box className='w-full lg:w-[59.5%]'>
          <Flex direction={'column'} align={'center'} justify={'center'} className='min-h-screen py-10 px-6'>
            <div className="w-full max-w-[450px] text-center">
              <img width={'300px'} className='mb-8 mx-auto' src={logoIMCAS} alt="" />
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
                <FaCheckCircle className="text-green-500 text-4xl mx-auto mb-4" />
                <Text size="6" weight="bold" className="text-green-700 mb-2 block">
                  Password Reset Successful!
                </Text>
                <Text size="3" className="text-green-600 mb-4 block">
                  {message}
                </Text>
                <Text size="2" className="text-gray-600">
                  You will be redirected to the login page in a few seconds...
                </Text>
              </div>

              <button
                onClick={() => navigate('/login')}
                className="w-full py-3 px-4 bg-[#0b5a97] text-white font-medium rounded-sm shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Go to Login
              </button>
            </div>
          </Flex>
        </Box>
      </Flex>
    );
  }

  return (
    <Flex direction={'row'} className='min-h-screen w-screen'>
      {/* Left decorative panel — hidden on mobile, visible on lg+ */}
      <Box className='hidden lg:flex h-screen bg-[#ecf2f1] w-[41.5%] sticky top-0'>
        <Flex gap={'6'} direction={'column'} align={'center'} className='h-full w-full'>
          <img className='w-[70%] my-24' src={loginLogo} alt="" />
          <img className='w-[88%] mt-0' src={signImage} alt="" />
        </Flex>
      </Box>
      {/* Right panel — full width on mobile */}
      <Box className='w-full lg:w-[59.5%]'>
        <Flex direction={'column'} align={'center'} justify={'center'} className='min-h-screen py-10 px-6'>
          <div className="w-full max-w-[450px]">
            <div className="flex flex-col mb-6">
              <img width={'300px'} className='mb-6' src={logoIMCAS} alt="" />
              <Text size="8" className="mb-2">Reset Password</Text>
              <Text size="2" className="text-gray-600">
                Enter your new password below.
              </Text>
            </div>

            {status === 'error' && (
              <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 flex items-center">
                <FaInfoCircle className="mr-2 flex-shrink-0" />
                <span>{message}</span>
              </div>
            )}

            <Formik
              initialValues={{ password: '', confirmPassword: '' }}
              validationSchema={resetPasswordSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting, values }) => (
                <Form>
                  <PasswordField
                    label="New Password"
                    name="password"
                    placeholder="Enter your new password"
                    showPassword={showPassword}
                    togglePassword={() => setShowPassword(!showPassword)}
                  />

                  <PasswordField
                    label="Confirm New Password"
                    name="confirmPassword"
                    placeholder="Confirm your new password"
                    showPassword={showConfirmPassword}
                    togglePassword={() => setShowConfirmPassword(!showConfirmPassword)}
                  />

                  {/* Password Requirements */}
                  <div className="mb-6 p-3 bg-gray-50 border border-gray-200 rounded-md">
                    <Text size="2" weight="bold" className="text-gray-700 mb-2 block">
                      Password Requirements:
                    </Text>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li className={values.password.length >= 8 ? 'text-green-600' : ''}>
                        • At least 8 characters
                      </li>
                      <li className={/[a-z]/.test(values.password) ? 'text-green-600' : ''}>
                        • One lowercase letter
                      </li>
                      <li className={/[A-Z]/.test(values.password) ? 'text-green-600' : ''}>
                        • One uppercase letter
                      </li>
                      <li className={/\d/.test(values.password) ? 'text-green-600' : ''}>
                        • One number
                      </li>
                      <li className={/[!@#$%^&*(),.?":{}|<>]/.test(values.password) ? 'text-green-600' : ''}>
                        • One special character
                      </li>
                    </ul>
                  </div>

                  <button
                    type="submit"
                    className="w-full py-3 px-4 bg-[#0b5a97] text-white font-medium rounded-sm shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isSubmitting || status === 'loading' || status === 'error'}
                  >
                    {status === 'loading' ? 'Resetting Password...' : 'Reset Password'}
                  </button>
                </Form>
              )}
            </Formik>
          </div>
        </Flex>
      </Box>
    </Flex>
  );
};

export default ResetPassword;
